import { useQuery } from "@tanstack/react-query";
import { objectToQueryString } from "../shared/utils";
import { useState, useEffect } from "react";

export async function request(endpoint, body = null, opts = {}) {
  const reqBody = body ? JSON.stringify(body) : null;

  const { headers = {}, ...rest } = opts;

  const res = await fetch(endpoint, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...headers,
    },
    body: reqBody,
    ...rest,
  });

  return jsonResponse(res, { endpoint });
}

export async function get(endpoint, params = {}, opts = {}) {
  const { headers = {}, ...rest } = opts;

  const res = await fetch(
    `${endpoint}${Object.keys(params).length > 0 ? `?${objectToQueryString(params)}` : ""}`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...headers,
      },
      ...rest,
    },
  );

  return jsonResponse(res, { endpoint });
}

export async function post(endpoint, body = {}, opts = {}) {
  const { headers = {}, ...rest } = opts;

  const res = await fetch(endpoint, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(body),
    method: "POST",
    ...rest,
  });

  return jsonResponse(res, { endpoint });
}

export async function put(endpoint, body = {}, opts = {}) {
  const { headers = {}, ...rest } = opts;

  const res = await fetch(endpoint, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(body),
    method: "PUT",
    ...rest,
  });

  return jsonResponse(res, { endpoint });
}

export async function del(endpoint, opts = {}) {
  const { headers = {}, ...rest } = opts;

  const res = await fetch(endpoint, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...headers,
    },
    method: "DELETE",
    ...rest,
  });

  return jsonResponse(res, { endpoint });
}

async function jsonResponse(response, { endpoint }) {
  if (!response.ok) {
    const error = new Error(`Error fetching ${endpoint}`);
    error.status = response.status;
    error.payload = await response.json().catch(() => null);
    throw error;
  }

  return response.json();
}

export function useGetQuery(queryKey, endpoint, params = {}, options = {}) {
  const qk = [...queryKey];

  if (params && Object.keys(params).length > 0) {
    qk.push(JSON.stringify(params));
  }

  const results = useQuery({
    queryKey: qk,
    queryFn: () => get(endpoint, params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 0,
    retry: 0,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
    ...options,
  });

  return { ...results, useGetQueryKey: qk };
}

export function useGetWithWait(queryKey, endpoint, params = {}, options = {}) {
  const qk = [...queryKey];
  const [lastValidData, setLastFetchedData] = useState(null);

  if (params && Object.keys(params).length > 0) {
    qk.push(JSON.stringify(params));
  }

  const results = useQuery({
    queryKey: qk,
    queryFn: () => get(endpoint, params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 0,
    retry: 0,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => {
      // If we're in a wait state, return the last valid data if available so that we don't show a totally blank state
      if (previousData?.wait) {
        return lastValidData;
      }
      return previousData;
    },
    refetchInterval: (query) => {
      // Don't keep retrying after unknown error
      if (query.state.error) {
        return false;
      }

      if (query.state.data?.wait) {
        return 1000;
      }

      return false;
    },
    refetchIntervalInBackground: true,
    ...options,
  });

  // Store the last valid data (non-null, non-wait)
  useEffect(() => {
    if (results.data && !results.data.wait) {
      setLastFetchedData(results.data);
    }
  }, [results.data, results.error]);

  // While we're waiting, make sure isFetching: true so that we show a loading state
  // and use the last valid data if available.
  if (results.data?.wait) {
    return {
      ...results,
      data: lastValidData,
      isFetching: true,
      isLoading: true,
    };
  }

  return results;
}

/**
 * Polls an endpoint until a condition is met or timeout is reached
 * @param {string} url - The URL to fetch
 * @param {function} conditionFn - Function that takes response data and returns true when polling should stop
 * @param {Object} options - Additional options
 * @param {number} options.interval - Polling interval in ms (default: 1000)
 * @param {number} options.timeout - Maximum polling time in ms (default: 31000)
 * @param {Object} options.fetchOptions - Options to pass to fetch
 * @returns {Promise} - Resolves with the final response data or rejects with error
 */
export async function fetchWithPolling(url, conditionFn, options = {}) {
  let asyncReportTimeout = undefined;
  if (window.currentAccount?.async_report_timeout_seconds) {
    asyncReportTimeout =
      1000 * (window.currentAccount.async_report_timeout_seconds + 60); // Add 60 sec buffer bc job might not get started right away
  }

  const interval = options.interval || 1000;
  const timeout = options.timeout || asyncReportTimeout || 31000;
  const fetchOptions = options.fetchOptions || {};

  const startTime = Date.now();

  while (true) {
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error(
        `fetchWithPolling request failed with status ${response.status}`,
      );
    }

    const data = await response.json();

    // Check if our condition is met
    if (conditionFn(data)) {
      return data;
    }

    // Check if we've exceeded the timeout
    if (Date.now() - startTime > timeout) {
      throw new Error("fetchWithPolling polling timeout exceeded");
    }

    // Wait for the specified interval before polling again
    await new Promise((resolve) => setTimeout(resolve, interval));
  }
}
